export type DailyPricePoint = {
  time: string;
  open?: number;
  high?: number;
  low?: number;
  close?: number;
};

export type DailySignalResult = {
  priceClose: number | null;
  atr14: number | null;
  priceCloseAboveMa: boolean;
  breakoutUp: boolean;
};

const averageSeries = (values: number[]) => {
  if (!values.length) {
    return null;
  }
  const total = values.reduce((sum, value) => sum + value, 0);
  return total / values.length;
};

const computeAtr = (series: DailyPricePoint[], windowSize: number) => {
  if (!series.length || series.length <= windowSize) {
    return null;
  }
  const ranges: number[] = [];
  for (let i = 1; i < series.length; i += 1) {
    const current = series[i];
    const previous = series[i - 1];
    if (
      current.high == null ||
      current.low == null ||
      current.close == null ||
      previous.close == null
    ) {
      continue;
    }
    const highLow = current.high - current.low;
    const highClose = Math.abs(current.high - previous.close);
    const lowClose = Math.abs(current.low - previous.close);
    ranges.push(Math.max(highLow, highClose, lowClose));
  }
  const recent = ranges.slice(-windowSize);
  return averageSeries(recent);
};

export const buildDailySignals = (
  dailyData: DailyPricePoint[],
  asOfDate?: string,
  thresholds?: { maDays?: number; breakoutLookbackDays?: number }
): DailySignalResult => {
  if (!dailyData.length || !asOfDate) {
    return {
      priceClose: null,
      atr14: null,
      priceCloseAboveMa: false,
      breakoutUp: false,
    };
  }
  const normalizedAsOf = asOfDate;
  const filtered = dailyData
    .filter((point) => point.time <= normalizedAsOf)
    .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
  if (!filtered.length) {
    return {
      priceClose: null,
      atr14: null,
      priceCloseAboveMa: false,
      breakoutUp: false,
    };
  }
  const closes = filtered
    .map((point) => (point.close == null ? null : Number(point.close)))
    .filter((value): value is number => value != null);
  const maDays = Math.max(1, Number(thresholds?.maDays) || 20);
  const breakoutLookbackDays = Math.max(1, Number(thresholds?.breakoutLookbackDays) || 20);
  const maSlice = closes.length >= maDays ? closes.slice(-maDays) : [];
  const maValue = averageSeries(maSlice);
  const latest = filtered[filtered.length - 1];
  const priceClose = latest.close == null ? null : Number(latest.close);
  let breakoutUp = false;
  if (closes.length > breakoutLookbackDays) {
    const prior = closes.slice(-breakoutLookbackDays - 1, -1);
    const maxPrior = Math.max(...prior);
    breakoutUp = priceClose != null && priceClose > maxPrior;
  }
  const atr14 = computeAtr(filtered, 14);
  return {
    priceClose,
    atr14,
    priceCloseAboveMa: maValue != null && priceClose != null ? priceClose > maValue : false,
    breakoutUp,
  };
};
